import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../components/Layout';
import { useAffiliatedHolders, useVisibleContacts } from '../hooks/useScopedData';
import { useVisibleUnitLeaders } from '../hooks/useUnits';
import { useLoginProfiles } from '../hooks/useUserAccess';
import {
  buildOrgChartForest,
  countSeats,
  countSeatsCouncil,
  holdersOf,
  holdersOfCouncilPosition,
  groupCouncilPositionsByArea,
  groupPositionsByCommittee,
  type OrgForestIssue,
  type OrgNode,
} from '../lib/orgChart';
import { assignCanonicalPerson, revokePositionAssignment, searchOrgChartPeople, updateContact, type OrgChartPersonMatch } from '../lib/db';
import {
  SERVES_OUTSIDE_LABEL,
  SERVES_OUTSIDE_TITLE,
  affiliatedHoldersOfSeat,
  outsideAssignState,
  rosterAssignErrorMessage,
} from '../lib/affiliatedHolders';
import { usePersonEntry } from '../components/PersonEntry';
import ManualEntryNotice from '../components/ManualEntryNotice';
import { canManageRoster, hasCouncilFullRead, isAdmin } from '../lib/visibility';
import { nodeToPdf, stamp } from '../lib/exporters';
import Drawer from '../components/Drawer';
import Spinner from '../components/Spinner';
import { OutsideDistrictAssign, OutsideDistrictHolders } from '../components/OutsideDistrictAssign';
import LeaderSearch from '../components/LeaderSearch';
import { EmailLink, PhoneLink } from '../components/ContactLink';
import {
  deriveAccess,
  findPositions,
  recName,
  type AffiliatedHolder,
  type AppSettings,
  type Contact,
  type Position,
  type UnitLeader,
  type UserProfile,
} from '../types';

type OrgView = 'chart' | 'roster';

/** Org Chart V2 — a top-down Chart view (built strictly from the configured
 *  Reports To relationships) alongside the original grouped-by-area Roster
 *  view. Both read the exact same Position/Contact data; the toggle only
 *  changes how it's presented — see AssignSeat below, shared by both views
 *  and both scopes, for the single assignment path that backs all four
 *  combinations (Council/District × Chart/Roster). */
type AssignSeat = { position: Position; district: string | null };

export default function OrgChart() {
  const { profile, settings, districtFilter, districts } = useAppContext();
  const contactsQ = useVisibleContacts(profile);
  // People who serve a district from outside it have no roster record there; this adds them to the seats they hold (empty while the
  // council has District Affiliations off, so nothing changes until then).
  const holdersQ = useAffiliatedHolders(profile);
  const loading = contactsQ.loading || holdersQ.loading;
  const admin = isAdmin(profile);
  const [view, setView] = useState<OrgView>('chart');
  const [assignSeat, setAssignSeat] = useState<AssignSeat | null>(null);
  const [exporting, setExporting] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  // Only admins can see the full users list (rules) or create one, so this
  // is what drives the "Give login access" shortcut — loaded only for them.
  const usersQ = useLoginProfiles(profile, admin);

  // Org Chart shows exactly one of Council or a single district at a time —
  // never a combined view. The top-bar picker's "All" option only means
  // "Council" for users who can actually see it (admin/council-wide); for
  // everyone else (no picker shown, districtFilter never leaves its default
  // 'All') it must keep meaning "my own district", never Council.
  const followsPicker = admin || hasCouncilFullRead(profile);
  const councilSelected = followsPicker && districtFilter === 'All';

  const district = useMemo(() => {
    const ok = (id: string | null | undefined) => !!id && districts.some((d) => d.id === id);
    if (!councilSelected && ok(districtFilter)) return districtFilter;
    if (ok(profile.districtId)) return profile.districtId as string;
    return districts[0]?.id ?? '';
  }, [districtFilter, councilSelected, profile.districtId, districts]);

  // Any Chair Position (Tier 2) or Key 3 (Tier 1) may assign/remove people
  // from this district's Org Chart — same authority as the Contacts screen,
  // scoped to whichever district is currently shown. Council-wide seat
  // management stays admin-only (see the two onSelect wirings below).
  const canManage = canManageRoster(profile, district);

  // Org Chart V2: Reports To — never committee nesting, Direct
  // Responsibility, Position names, or organization level — is the only
  // input to the visual tree. Council and District Positions are two
  // separate forests, built and rendered independently, so one scope's
  // reporting structure can never leak into the other's chart.
  const districtPositions = useMemo(
    () => settings.positions.filter((p) => (p.organizationLevel ?? 'district') === 'district'),
    [settings.positions],
  );
  const councilPositions = useMemo(
    () => settings.positions.filter((p) => p.organizationLevel === 'council'),
    [settings.positions],
  );
  const districtChart = useMemo(() => buildOrgChartForest(districtPositions), [districtPositions]);
  const councilChart = useMemo(() => buildOrgChartForest(councilPositions), [councilPositions]);

  const contacts = contactsQ.data;
  const leadersQ = useVisibleUnitLeaders(profile);
  // SYT is where phone actually lives for a registered leader — unitLeaders
  // itself has no phone field. Joined by memberId only here, to backfill a
  // new/linked Contact's phone when picked via the leader-search below.
  const districtSeats = useMemo(
    () => countSeats(districtChart.forest, contacts, district, holdersQ.holders),
    [districtChart, contacts, district, holdersQ.holders],
  );
  const councilSeats = useMemo(
    () => countSeatsCouncil(councilChart.forest, contacts),
    [councilChart, contacts],
  );

  const dName = recName(settings.districts, district);
  const scopeName = councilSelected ? 'Council' : dName;

  const exportPdf = async () => {
    if (!chartRef.current) return;
    setExporting(true);
    chartRef.current.classList.add('org-exporting');
    try {
      const seats = councilSelected ? councilSeats : districtSeats;
      const viewLabel = view === 'chart' ? 'org chart' : 'roster';
      const title = `${scopeName} ${viewLabel} · ${seats.filled}/${seats.seats} seats filled · ${stamp()}`;
      const filename = `org-${view}-${slug(scopeName)}-${stamp()}.pdf`;
      await nodeToPdf(chartRef.current, filename, title);
    } finally {
      chartRef.current?.classList.remove('org-exporting');
      setExporting(false);
    }
  };

  return (
    <>
      <section className="iv-intro">
        <div>
          <h2>Org Chart</h2>
          <p>
            Chart shows the top-down reporting structure; Roster groups every Position by
            organizational area. Both reflect the same Positions and people.
          </p>
        </div>
        <button
          className="btn"
          onClick={exportPdf}
          disabled={exporting || loading}
          title={`Downloads the current ${scopeName} ${view === 'chart' ? 'org chart' : 'roster'} as a PDF`}
        >
          {exporting ? <Spinner size={14} /> : 'Download PDF'}
        </button>
      </section>

      <GuidanceBox admin={admin} />

      {!loading && (
        <div className="set-subtabs" role="tablist" aria-label="Org Chart view">
          <button
            type="button"
            className={`set-subtab${view === 'chart' ? ' active' : ''}`}
            onClick={() => setView('chart')}
          >
            Chart
          </button>
          <button
            type="button"
            className={`set-subtab${view === 'roster' ? ' active' : ''}`}
            onClick={() => setView('roster')}
          >
            Roster
          </button>
        </div>
      )}

      {loading && <div className="empty"><Spinner label="Loading…" /></div>}

      <div ref={chartRef}>
        {!loading && councilSelected && view === 'chart' && (
          <ChartDoc
            scopeLabel="Council"
            scopeName="Council"
            seats={councilSeats}
            issues={councilChart.issues}
            forest={councilChart.forest}
            resolveHolders={(name) => holdersOfCouncilPosition(name, contacts)}
            settings={settings}
            admin={admin}
            onSelect={admin ? (p) => setAssignSeat({ position: p, district: null }) : undefined}
          />
        )}
        {!loading && councilSelected && view === 'roster' && (
          <CouncilSection
            settings={settings}
            contacts={contacts}
            canManage={admin}
            onAssign={(p) => setAssignSeat({ position: p, district: null })}
          />
        )}
        {!loading && !councilSelected && view === 'chart' && (
          <ChartDoc
            scopeLabel="District"
            scopeName={dName}
            seats={districtSeats}
            issues={districtChart.issues}
            forest={districtChart.forest}
            resolveHolders={(name) => holdersOf(name, contacts, district)}
            resolveAffiliated={(p) => affiliatedHoldersOfSeat(holdersQ.holders, p.positionId, district)}
            settings={settings}
            admin={admin}
            onSelect={canManage ? (p) => setAssignSeat({ position: p, district }) : undefined}
          />
        )}
        {!loading && !councilSelected && view === 'roster' && (
          <DistrictRoster
            positions={districtPositions}
            district={district}
            dName={dName}
            seats={districtSeats}
            contacts={contacts}
            affiliated={holdersQ.holders}
            settings={settings}
            canManage={canManage}
            onAssign={(p) => setAssignSeat({ position: p, district })}
          />
        )}
      </div>

      {assignSeat && assignSeat.district === null && (
        <CouncilAssignDrawer
          position={assignSeat.position}
          contacts={contacts}
          settings={settings}
          onClose={() => setAssignSeat(null)}
        />
      )}
      {assignSeat && assignSeat.district !== null && (
        <AssignDrawer
          position={assignSeat.position}
          contacts={contacts}
          leaders={leadersQ.data.filter((l) => l.districtId === assignSeat.district)}
          affiliated={affiliatedHoldersOfSeat(holdersQ.holders, assignSeat.position.positionId, assignSeat.district)}
          outsideEnabled={holdersQ.enabled}
          district={assignSeat.district}
          settings={settings}
          users={usersQ.data}
          isAdminUser={admin}
          onClose={() => setAssignSeat(null)}
        />
      )}
    </>
  );
}

const slug = (s: string) => s.toLowerCase().replace(/\s+/g, '-');

/** Org Chart V2 — concise, always-visible pointer to where the structure
 *  shown here actually comes from. The explanation is for everyone; the
 *  deep links into Settings (an admin-only route — see App.tsx) are
 *  admin-gated so a non-admin never sees a link they can't use. */
function GuidanceBox({ admin }: { admin: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="org-guidance">
      <b>How this chart is configured</b>
      <p>
        Positions, reporting relationships (Reports To), and responsibilities are managed in
        Settings → Positions &amp; organization. Organizational areas are managed in Settings → App
        Settings → Committees — Council and District each use their own separate areas, and areas
        group Positions, they don't set who reports to whom. Assignments can be made here or in
        Settings. Permissions are configured separately and are never inherited from this structure.
      </p>
      {admin && (
        <div className="org-guidance-links">
          <button
            type="button"
            className="linkbtn"
            onClick={() => navigate('/settings', { state: { openTab: 'permissions' } })}
          >
            Open Positions &amp; organization →
          </button>
          <button type="button" className="linkbtn" onClick={() => navigate('/settings')}>
            Open Committees →
          </button>
        </div>
      )}
    </div>
  );
}

/** Org Chart V2 — surfaces buildOrgChartForest()'s issues (a duplicate,
 *  self-reference, missing parent, or reporting cycle) instead of letting
 *  malformed Reports To data fail silently. Nothing is ever dropped — the
 *  affected Position is simply shown as top-level, and that's stated here. */
function IssueBanner({ issues, admin }: { issues: OrgForestIssue[]; admin: boolean }) {
  const navigate = useNavigate();
  if (issues.length === 0) return null;
  return (
    <div className="org-issue-banner">
      <b>
        {issues.length} Position{issues.length === 1 ? ' has' : 's have'} a Reports To configuration
        issue
      </b>
      Nothing was dropped — each is shown as a top-level Position below until fixed.
      <ul>
        {issues.map((i) => (
          <li key={`${i.type}-${i.positionId}`}>
            <b>{i.positionName}</b>: {i.detail}
          </li>
        ))}
      </ul>
      {admin && (
        <button
          type="button"
          className="linkbtn"
          onClick={() => navigate('/settings', { state: { openTab: 'permissions' } })}
        >
          Fix in Settings → Positions &amp; organization →
        </button>
      )}
    </div>
  );
}

// ============================================================
// Council Organization Phase B — council-level Positions.
// Roster is the original grouped-by-area presentation; Chart (in ChartDoc,
// below) is Org Chart V2's addition — both read the same
// settings.positions/contacts, just presented differently. Organizational
// placement only — see deriveAccess()'s doc comment (src/types.ts) for why
// holding one of these never grants V1 committee/chair authority.
// ============================================================

function CouncilSection({
  settings,
  contacts,
  canManage,
  onAssign,
}: {
  settings: AppSettings;
  contacts: Contact[];
  canManage: boolean;
  onAssign: (p: Position) => void;
}) {
  const byArea = useMemo(() => groupCouncilPositionsByArea(settings.positions), [settings.positions]);

  if (byArea.size === 0) return null;

  const areaName = (id: string) => (id ? recName(settings.committees, id) : 'Council Leadership');
  const areaIds = [...byArea.keys()].sort((a, b) => areaName(a).localeCompare(areaName(b)));

  return (
    <div className="org-section">
      <h3 className="org-section-head">Council</h3>
      {areaIds.map((areaId) => (
        <div key={areaId || '__top__'} className="card" style={{ marginBottom: 12 }}>
          <div className="set-cardhead">
            <div>
              <h2 style={{ fontSize: 15 }}>{areaName(areaId)}</h2>
            </div>
          </div>
          <div className="set-list">
            {byArea.get(areaId)!.map((p) => {
              const holders = holdersOfCouncilPosition(p.name, contacts).filter(
                (c) => c.name && !/vacant/i.test(c.name),
              );
              return (
                <div key={p.positionId} className="set-row">
                  <div className="set-row-main">
                    <div className="set-name">{p.name}</div>
                    <div className="subtitle">
                      {holders.length
                        ? holders.map((h) => (h.districtId ? `${h.name} (${recName(settings.districts, h.districtId)})` : h.name)).join(', ')
                        : 'Vacant'}
                    </div>
                  </div>
                  {canManage && (
                    <div className="set-row-actions">
                      <button className="linkbtn" onClick={() => onAssign(p)}>
                        Assign
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Org Chart V2 — the Roster view for a district: every district Position
 *  grouped by its committee/organizational area, the same shape
 *  CouncilSection already uses for Council. Assignment reuses the richer,
 *  already-existing district AssignDrawer (leader search, add-new-person,
 *  give-login) via the shared onAssign callback — no separate district
 *  assign UI was built for this. */
function DistrictRoster({
  positions,
  district,
  dName,
  seats,
  contacts,
  affiliated,
  settings,
  canManage,
  onAssign,
}: {
  positions: Position[];
  district: string;
  dName: string;
  seats: { filled: number; seats: number };
  contacts: Contact[];
  affiliated: readonly AffiliatedHolder[];
  settings: AppSettings;
  canManage: boolean;
  onAssign: (p: Position) => void;
}) {
  const byArea = useMemo(() => groupPositionsByCommittee(positions), [positions]);
  const areaName = (id: string) => (id ? recName(settings.committees, id) : 'District Leadership');
  const areaIds = [...byArea.keys()].sort((a, b) => areaName(a).localeCompare(areaName(b)));

  return (
    <div className="org-doc">
      <div className="card" style={{ display: 'flex', gap: 24, alignItems: 'baseline' }}>
        <div>
          <div className="subtitle">District</div>
          <b style={{ fontSize: 16 }}>{dName}</b>
        </div>
        <div>
          <div className="subtitle">Seats filled</div>
          <b style={{ fontSize: 16 }}>
            {seats.filled} / {seats.seats}
          </b>
        </div>
      </div>

      <div className="org-section">
        {areaIds.map((areaId) => (
          <div key={areaId || '__top__'} className="card" style={{ marginBottom: 12 }}>
            <div className="set-cardhead">
              <div>
                <h2 style={{ fontSize: 15 }}>{areaName(areaId)}</h2>
              </div>
            </div>
            <div className="set-list">
              {byArea.get(areaId)!.map((p) => {
                const holders = holdersOf(p.name, contacts, district).filter(
                  (c) => c.name && !/vacant/i.test(c.name),
                );
                const fromOutside = affiliatedHoldersOfSeat(affiliated, p.positionId, district);
                return (
                  <div key={p.positionId} className="set-row">
                    <div className="set-row-main">
                      <div className="set-name">
                        {p.name}
                        {p.key3 && <span className="org-tag">key 3</span>}
                        {p.lead && <span className="org-tag">lead</span>}
                      </div>
                      <div className="subtitle">
                        {holders.length + fromOutside.length === 0 && 'Vacant'}
                        {holders.map((h) => h.name).join(', ')}
                        {holders.length > 0 && fromOutside.length > 0 && ', '}
                        {fromOutside.map((h, i) => (
                          <span key={h.assignmentId}>
                            {i > 0 && ', '}
                            {h.name} <span className="org-tag" title={SERVES_OUTSIDE_TITLE}>{SERVES_OUTSIDE_LABEL}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    {canManage && (
                      <div className="set-row-actions">
                        <button className="linkbtn" onClick={() => onAssign(p)}>
                          Assign
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Council-wide seat assignment — the council counterpart to AssignDrawer,
 *  deliberately simpler and NOT district-filtered (a council Position's
 *  holder can be rostered in any district). Reuses the exact same
 *  deriveAccess()-recomputes-committees write pattern AssignDrawer already
 *  uses below, which (per deriveAccess's own filtering) correctly leaves a
 *  Contact's derived committees untouched by a council Position. Shared by
 *  both the Council Chart and Council Roster views (see AssignSeat above). */
function CouncilAssignDrawer({
  position,
  contacts,
  settings,
  onClose,
}: {
  position: Position;
  contacts: Contact[];
  settings: AppSettings;
  onClose: () => void;
}) {
  const held = holdersOfCouncilPosition(position.name, contacts).filter(
    (c) => c.name && !/vacant/i.test(c.name),
  );
  const heldIds = new Set(held.map((c) => c.id));
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const matches = query.trim()
    ? contacts
        .filter((c) => !heldIds.has(c.id) && c.name.toLowerCase().includes(query.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  // A council-wide seat must be able to find any eligible active canonical
  // Person tenant-wide, not only someone who already has a Contact record —
  // the `matches` filter above can never surface a Person with no Contact
  // at all. This is a second, independent candidate source (searchOrgChartPeopleV1,
  // built in Phase 1, not previously wired to any UI); the two lists are
  // shown side by side rather than merged, since a Contact carries no
  // client-visible personId to dedupe against.
  const [personMatches, setPersonMatches] = useState<OrgChartPersonMatch[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setPersonMatches([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchOrgChartPeople(q)
        .then((people) => { if (!cancelled) setPersonMatches(people.slice(0, 8)); })
        .catch(() => { if (!cancelled) setPersonMatches([]); });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const assignPerson = async (person: OrgChartPersonMatch) => {
    setBusyId(person.personId);
    setErr(null);
    try {
      await assignCanonicalPerson({
        positionId: position.positionId,
        districtId: null,
        personId: person.personId,
        reason: `Assigned to ${position.name} via the Council Org Chart`,
      });
      setQuery('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not assign.');
    } finally {
      setBusyId(null);
    }
  };

  const withPosition = (c: Contact, add: boolean) => {
    const positions = add
      ? [...new Set([...c.positions, position.name])]
      : c.positions.filter((n) => n !== position.name);
    const access = deriveAccess(findPositions(settings.positions, positions), settings.committees);
    return { positions, committees: access.committees };
  };

  const assign = async (c: Contact) => {
    setBusyId(c.id);
    setErr(null);
    try {
      await updateContact(c.id, withPosition(c, true));
      setQuery('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not assign.');
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (c: Contact) => {
    setBusyId(c.id);
    setErr(null);
    try {
      await updateContact(c.id, withPosition(c, false));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not remove.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Drawer title={position.name} subtitle="Council-wide seat — search across every district's roster." onClose={onClose}>
      {held.length > 0 && (
        <div className="people-chips">
          {held.map((c) => (
            <span key={c.id} className="people-chip">
              {c.name}{c.districtId ? ` · ${recName(settings.districts, c.districtId)}` : ''}
              <button
                type="button"
                className="people-chip-remove"
                aria-label={`Remove ${c.name}`}
                disabled={busyId === c.id}
                onClick={() => void remove(c)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <input
          placeholder="Search people to add…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query.trim() && (
          <div className="people-dropdown">
            {matches.map((c) => (
              <button
                key={c.id}
                type="button"
                className="people-option"
                disabled={busyId === c.id}
                onClick={() => void assign(c)}
              >
                {c.name}{c.districtId ? ` — ${recName(settings.districts, c.districtId)}` : ' — council level'}
              </button>
            ))}
            {personMatches.map((p) => (
              <button
                key={p.personId}
                type="button"
                className="people-option"
                disabled={busyId === p.personId}
                onClick={() => void assignPerson(p)}
              >
                {p.displayName}
                {p.status === 'provisional' ? ' (unconfirmed identity)' : ''}
                {p.evidence.length > 0 ? ` — ${p.evidence.slice(0, 2).join(', ')}` : ''}
              </button>
            ))}
            {matches.length === 0 && personMatches.length === 0 && <div className="people-option-empty">No matches.</div>}
          </div>
        )}
      </div>
      {err && <p className="err">{err}</p>}
    </Drawer>
  );
}

/** Org Chart V2's Chart view — the header (scope + seats), the malformed-
 *  structure banner, the Key 3 band, and every reporting tree, built
 *  strictly from Reports To (buildOrgChartForest). Scope-agnostic: the
 *  same component renders both the Council chart and a District chart —
 *  the caller supplies which Positions, whose holders, and what to call
 *  it. Rendered live for the selected scope and off-screen for PDF export. */
function ChartDoc({
  scopeLabel,
  scopeName,
  seats,
  issues,
  forest,
  resolveHolders,
  resolveAffiliated,
  settings,
  admin,
  onSelect,
}: {
  scopeLabel: string;
  scopeName: string;
  seats: { filled: number; seats: number };
  issues: OrgForestIssue[];
  forest: OrgNode[];
  resolveHolders: (positionName: string) => Contact[];
  /** Outside-district holders of a seat (district charts only). Absent = none. */
  resolveAffiliated?: (p: Position) => AffiliatedHolder[];
  settings: AppSettings;
  admin: boolean;
  onSelect?: (p: Position) => void;
}) {
  const key3Roots = forest.filter((n) => n.position.key3);
  const otherRoots = forest.filter((n) => !n.position.key3);

  return (
    <div className="org-doc">
      <div className="card" style={{ display: 'flex', gap: 24, alignItems: 'baseline' }}>
        <div>
          <div className="subtitle">{scopeLabel}</div>
          <b style={{ fontSize: 16 }}>{scopeName}</b>
        </div>
        <div>
          <div className="subtitle">Seats filled</div>
          <b style={{ fontSize: 16 }}>
            {seats.filled} / {seats.seats}
          </b>
        </div>
      </div>

      <IssueBanner issues={issues} admin={admin} />

      {forest.length === 0 && issues.length === 0 && (
        <div className="empty">No Positions configured yet for {scopeName}.</div>
      )}

      {key3Roots.length > 0 && (
        <div className="org-section">
          <h3 className="org-section-head">Key 3</h3>
          <div className="org-band">
            {key3Roots.map((n) => (
              <PositionCard
                key={n.position.positionId}
                node={n}
                resolveHolders={resolveHolders}
                resolveAffiliated={resolveAffiliated}
                settings={settings}
                emphasize
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      )}

      {key3Roots
        .filter((n) => n.children.length > 0)
        .map((n) => (
          <TreeSection
            key={n.position.positionId}
            root={n}
            resolveHolders={resolveHolders}
            resolveAffiliated={resolveAffiliated}
            settings={settings}
            onSelect={onSelect}
          />
        ))}

      {otherRoots.map((n) => (
        <TreeSection
          key={n.position.positionId}
          root={n}
          resolveHolders={resolveHolders}
          resolveAffiliated={resolveAffiliated}
          settings={settings}
          independent={otherRoots.length > 1}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function TreeSection({
  root,
  resolveHolders,
  resolveAffiliated,
  settings,
  independent,
  onSelect,
}: {
  root: OrgNode;
  resolveHolders: (positionName: string) => Contact[];
  resolveAffiliated?: (p: Position) => AffiliatedHolder[];
  settings: AppSettings;
  independent?: boolean;
  onSelect?: (p: Position) => void;
}) {
  return (
    <div className="org-section">
      <h3 className="org-section-head">
        {root.position.name}
        {independent && <span className="org-tag">independent</span>}
      </h3>
      <OrgBranch node={root} resolveHolders={resolveHolders} resolveAffiliated={resolveAffiliated} settings={settings} onSelect={onSelect} />
    </div>
  );
}

function OrgBranch({
  node,
  resolveHolders,
  resolveAffiliated,
  settings,
  onSelect,
}: {
  node: OrgNode;
  resolveHolders: (positionName: string) => Contact[];
  resolveAffiliated?: (p: Position) => AffiliatedHolder[];
  settings: AppSettings;
  onSelect?: (p: Position) => void;
}) {
  return (
    <div className="org-node">
      <PositionCard node={node} resolveHolders={resolveHolders} resolveAffiliated={resolveAffiliated} settings={settings} onSelect={onSelect} />
      {node.children.length > 0 && (
        <div className="org-children">
          {node.children.map((c) => (
            <OrgBranch
              key={c.position.positionId}
              node={c}
              resolveHolders={resolveHolders}
              resolveAffiliated={resolveAffiliated}
              settings={settings}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PositionCard({
  node,
  resolveHolders,
  resolveAffiliated,
  settings,
  emphasize,
  onSelect,
}: {
  node: OrgNode;
  resolveHolders: (positionName: string) => Contact[];
  resolveAffiliated?: (p: Position) => AffiliatedHolder[];
  settings: AppSettings;
  emphasize?: boolean;
  onSelect?: (p: Position) => void;
}) {
  const p = node.position;
  const held = resolveHolders(p.name);
  const real = held.filter((c) => c.name && !/vacant/i.test(c.name));
  // Someone who serves this district from outside it has no roster record here; they fill the seat all the same.
  const fromOutside = resolveAffiliated ? resolveAffiliated(p) : [];
  const tags = [
    p.committee ? recName(settings.committees, p.committee) : null,
    p.districtWide ? 'district-wide' : null,
    p.lead ? 'lead' : null,
  ].filter(Boolean) as string[];

  return (
    <div
      className={`org-card${emphasize ? ' is-key3' : ''}${real.length + fromOutside.length === 0 ? ' is-vacant' : ''}${onSelect ? ' is-clickable' : ''}`}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? () => onSelect(p) : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(p);
              }
            }
          : undefined
      }
    >
      <div className="org-card-title">
        {p.name}
        {tags.map((t) => (
          <span key={t} className="org-tag">
            {t}
          </span>
        ))}
      </div>
      {real.length + fromOutside.length === 0 ? (
        <div className="org-vacant-line">Vacant</div>
      ) : (
        <>
          {real.map((c) => (
            <div key={c.id} className="org-holder">
              <b>{c.name}</b>
              {(c.email || c.phone) && (
                <span className="org-contact">
                  <EmailLink email={c.email} />
                  {c.email && c.phone ? ' · ' : ''}
                  <PhoneLink phone={c.phone} />
                </span>
              )}
            </div>
          ))}
          {fromOutside.map((h) => (
            <div key={h.assignmentId} className="org-holder">
              <b>{h.name}</b>
              <span className="org-tag" title={SERVES_OUTSIDE_TITLE}>{SERVES_OUTSIDE_LABEL}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function AssignDrawer({
  position,
  contacts,
  leaders,
  affiliated,
  outsideEnabled,
  district,
  settings,
  users,
  isAdminUser,
  onClose,
}: {
  position: Position;
  contacts: Contact[];
  leaders: UnitLeader[];
  /** People who hold this seat from outside the district (no roster record here). */
  affiliated: AffiliatedHolder[];
  /** Whether this council has made District Affiliations available. */
  outsideEnabled: boolean;
  district: string;
  settings: AppSettings;
  users: UserProfile[];
  isAdminUser: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { enter: enterPerson, dialog: personEntryDialog } = usePersonEntry();
  const held = holdersOf(position.name, contacts, district).filter(
    (c) => c.name && !/vacant/i.test(c.name),
  );
  const heldIds = new Set(held.map((c) => c.id));
  const others = contacts
    .filter((c) => c.districtId === district && !heldIds.has(c.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const contactHasLogin = (c: Contact) => users.some((u) => u.contactId === c.id);

  const giveLogin = (person: { id: string; name: string; email?: string; positions: string[] }) => {
    navigate('/settings', {
      state: {
        openUserPrefill: {
          email: person.email ?? '',
          displayName: person.name,
          districtId: district,
          positions: person.positions,
          contactId: person.id,
        },
      },
    });
  };

  /** A contact's positions with this seat added or removed, recomputed as
   *  the full derived set (committees are a union across everything they
   *  hold, so this can't be a partial patch). */
  const withPosition = (c: Contact, add: boolean) => {
    const positions = add
      ? [...new Set([...c.positions, position.name])]
      : c.positions.filter((n) => n !== position.name);
    const access = deriveAccess(findPositions(settings.positions, positions), settings.committees);
    return { positions, committees: access.committees };
  };

  const [busyId, setBusyId] = useState<string | null>(null);
  const [pickId, setPickId] = useState('');
  const [adding, setAdding] = useState(false);
  const [manualNotice, setManualNotice] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newGivesLogin, setNewGivesLogin] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const remove = async (c: Contact) => {
    setBusyId(c.id);
    setErr(null);
    try {
      await updateContact(c.id, withPosition(c, false));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not remove.');
    } finally {
      setBusyId(null);
    }
  };

  const assign = async () => {
    const c = others.find((x) => x.id === pickId);
    if (!c) return;
    setBusyId(c.id);
    setErr(null);
    try {
      await updateContact(c.id, withPosition(c, true));
      setPickId('');
    } catch (e) {
      setErr(rosterAssignErrorMessage(e, affiliated.map((h) => h.name)));
    } finally {
      setBusyId(null);
    }
  };

  const addNew = async () => {
    if (!newName.trim()) return;
    setBusyId('new');
    setErr(null);
    try {
      const access = deriveAccess(findPositions(settings.positions, [position.name]), settings.committees);
      const name = newName.trim();
      const email = newEmail.trim();
      // Person first: the server names/creates the Person (asking about possible duplicates) and the roster
      // record is created for it. Nothing is matched by name or email.
      const entry = await enterPerson({
        districtId: district,
        positions: [position.name],
        committees: access.committees,
        name,
        email,
      });
      if (entry.status === 'cancelled') return;
      if (entry.status === 'exists') {
        setErr(`${name} is already on the roster. Pick them from the list above instead.`);
        return;
      }
      const contactId = entry.contactId;
      if (newGivesLogin) {
        giveLogin({ id: contactId, name, email, positions: [position.name] });
        return;
      }
      setNewName('');
      setNewEmail('');
      setNewGivesLogin(false);
      setAdding(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not add.');
    } finally {
      setBusyId(null);
    }
  };

  /** Pull someone from the imported leader roster onto this seat. The leader is identified by their stable
   *  my.Scouting Member ID — resolved to their Person on the server — never by matching an email or a name.
   *  If that Person already has a roster record in this district, the seat is added to it; otherwise a
   *  roster record is created for the Person (name, email and phone come from the Person). */
  const assignFromLeader = async (leader: UnitLeader) => {
    setBusyId('leader');
    setErr(null);
    try {
      const access = deriveAccess(findPositions(settings.positions, [position.name]), settings.committees);
      const entry = await enterPerson(
        { districtId: district, positions: [position.name], committees: access.committees, name: leader.fullName, email: leader.email },
        { memberId: leader.memberId },
      );
      if (entry.status === 'cancelled') return;
      if (entry.status === 'exists') {
        const existing = contacts.find((c) => c.id === entry.contactId);
        if (!existing || existing.districtId !== district) {
          setErr(`${leader.fullName} is already on the roster${existing ? ' in another district' : ''}. Ask a roster manager to add this seat to their record.`);
          return;
        }
        await updateContact(existing.id, withPosition(existing, true));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not assign.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Drawer title={position.name} subtitle={recName(settings.districts, district)} onClose={onClose}>
      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
        This changes the district roster only — it doesn't affect who can sign in to Compass.
        That's still set up separately in Settings → Users &amp; Access.
      </div>

      {held.length > 0 ? (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 6 }}>Currently holds this seat</div>
          {held.map((c) => (
            <div
              key={c.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 0',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <div>
                <b>{c.name}</b>
                {c.email && <div style={{ fontSize: 12, color: 'var(--muted)' }}><EmailLink email={c.email} /></div>}
                {isAdminUser && !contactHasLogin(c) && (
                  <button className="linkbtn" style={{ fontSize: 12 }} onClick={() => giveLogin(c)}>
                    Give login access →
                  </button>
                )}
              </div>
              <button className="btn" disabled={busyId === c.id} onClick={() => remove(c)}>
                {busyId === c.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      ) : affiliated.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>Vacant.</div>
      ) : null}

      <OutsideDistrictHolders
        holders={affiliated}
        isAdmin={isAdminUser}
        onRemove={(h) => revokePositionAssignment({
          positionId: position.positionId,
          districtId: district,
          personId: h.personId,
          reason: `Removed from ${position.name} via the District Org Chart`,
        })}
      />

      <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 2 }}>Assign someone already on the roster</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
        Adds this seat to whatever else they already hold — doesn't replace it.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <select value={pickId} onChange={(e) => setPickId(e.target.value)} style={{ flex: 1 }}>
          <option value="">Choose a person…</option>
          {others.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.positions.length ? ` — also ${c.positions.join(', ')}` : ''}
            </option>
          ))}
        </select>
        <button className="btn primary" disabled={!pickId || busyId === pickId} onClick={assign}>
          {busyId === pickId ? 'Assigning…' : 'Assign'}
        </button>
      </div>
      {others.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
          No one else on this district's roster yet — search the leader directory or add a new
          person below.
        </div>
      )}

      {leaders.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 2 }}>
            Or pull someone from the leader directory
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
            Registered leaders in this district — imported from the Trained Leaders report.
            {busyId === 'leader' ? ' Assigning…' : ''}
          </div>
          <LeaderSearch
            leaders={leaders}
            placeholder="Search this district's leaders…"
            districtName={() => recName(settings.districts, district)}
            onPick={(l) => void assignFromLeader(l)}
          />
        </div>
      )}

      <OutsideDistrictAssign
        state={outsideAssignState({
          admin: isAdminUser,
          enabled: outsideEnabled,
          position,
          rosterHolderCount: held.length,
          affiliatedHolderCount: affiliated.length,
        })}
        positionName={position.name}
        districtName={recName(settings.districts, district)}
        search={searchOrgChartPeople}
        onAssign={(person) => assignCanonicalPerson({
          positionId: position.positionId,
          districtId: district,
          personId: person.personId,
          reason: `Assigned to ${position.name} (serving from outside the district) via the District Org Chart`,
        })}
      />

      {adding ? (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 8 }}>New person</div>
          <input
            placeholder="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
          />
          <input
            placeholder="Email (optional)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            style={{ width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
          />
          {isAdminUser && (
            <label className="checkbox-row" style={{ marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={newGivesLogin}
                onChange={(e) => setNewGivesLogin(e.target.checked)}
              />
              Also give them Compass login access
            </label>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn primary" disabled={!newName.trim() || busyId === 'new'} onClick={addNew}>
              {busyId === 'new'
                ? 'Adding…'
                : newGivesLogin
                  ? 'Add & continue to login setup'
                  : 'Add & assign'}
            </button>
            <button className="btn" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="linkbtn" onClick={() => setManualNotice(true)}>
          + Add Person Manually to this seat
        </button>
      )}

      {err && (
        <div className="err" style={{ marginTop: 12 }}>
          {err}
        </div>
      )}
      {manualNotice && (
        <ManualEntryNotice
          onCancel={() => setManualNotice(false)}
          onProceed={() => { setManualNotice(false); setAdding(true); }}
        />
      )}
      {personEntryDialog}
    </Drawer>
  );
}
